import { cn } from '@/lib/cn';
import { useState, type ReactNode } from 'react';
import { type Editor, EditorContent } from '@tiptap/react';
import { FormatSection } from '@/lib/notification-editor/components/minimal-tiptap/components/section/format-section';
import { Separator } from '@/components/ui/separator';
import { TextStyleSection } from '@/lib/notification-editor/components/minimal-tiptap/components/section/font-style-section';
import { ListSection } from '@/lib/notification-editor/components/minimal-tiptap/components/section/list-section';
import { LinkEditPopover } from '@/lib/notification-editor/components/minimal-tiptap/components/link/link-edit-popover';
import { TextArea } from '@/components/ui/textarea';
import { Eye, SquareSplitHorizontal } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { BaseInstance } from '@pigello/pigello-matrix';
import type { FieldProps } from '../../types';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import type { ControllerRenderProps, FieldValues, Path } from 'react-hook-form';
import { DescriptionTooltip } from '../description-tooltip';
import { useMarkdownTiptap } from './hooks';

type RawState = 'none' | 'split' | 'full';

interface MarkdownEditorContentProps {
  field: ControllerRenderProps<FieldValues, string>;
  placeholder: string | undefined;
  maxLength: number | undefined;
  disabled: boolean | undefined;
  onValueChange: ((value: string) => void) | undefined;
}

const CustomMarkdownToolbarButton = ({
  children,
  stateToHandle,
  tooltip,
  setRawState,
  rawState,
}: {
  children: ReactNode;
  stateToHandle: RawState;
  tooltip: string;
  setRawState: (state: RawState) => void;
  rawState: RawState;
}) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type='button'
          onClick={() => setRawState(stateToHandle)}
          className={cn(
            'm-1 rounded-md p-1 hover:text-muted-foreground',
            rawState === stateToHandle && 'bg-accent'
          )}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <div className='flex flex-col items-center text-center'>{tooltip}</div>
      </TooltipContent>
    </Tooltip>
  );
};

const MarkdownToolbar = ({
  editor,
  setRawState,
  rawState,
}: {
  editor: Editor;
  setRawState: (state: RawState) => void;
  rawState: RawState;
}) => {
  return (
    <div className='flex items-center gap-1 rounded-t-lg border border-b-0 bg-background/50 px-2'>
      <FormatSection editor={editor} activeLevels={[1, 2, 3]} />
      <Separator orientation='vertical' className='mx-2 h-10' />
      <TextStyleSection
        editor={editor}
        activeActions={['bold', 'italic', 'underline']}
        mainActionCount={3}
      />
      <Separator orientation='vertical' className='mx-2 h-10' />
      <ListSection
        editor={editor}
        activeActions={['orderedList', 'bulletList']}
        mainActionCount={2}
      />
      <Separator orientation='vertical' className='mx-2 h-10' />
      <LinkEditPopover editor={editor} />
      <Separator orientation='vertical' className='mx-2 h-10' />
      <CustomMarkdownToolbarButton
        stateToHandle='none'
        tooltip='Visualisering'
        setRawState={setRawState}
        rawState={rawState}
      >
        <Eye />
      </CustomMarkdownToolbarButton>
      <CustomMarkdownToolbarButton
        stateToHandle='split'
        tooltip='Delad vy'
        setRawState={setRawState}
        rawState={rawState}
      >
        <SquareSplitHorizontal />
      </CustomMarkdownToolbarButton>
      <CustomMarkdownToolbarButton
        stateToHandle='full'
        tooltip='Markdown'
        setRawState={setRawState}
        rawState={rawState}
      >
        Raw
      </CustomMarkdownToolbarButton>
    </div>
  );
};

function MarkdownEditorContent({
  field,
  placeholder,
  maxLength,
  disabled,
  onValueChange,
}: MarkdownEditorContentProps) {
  const [showRawMarkdownState, setShowRawMarkdownState] =
    useState<RawState>('none');

  const [localValue, setLocalValue] = useState(field.value ?? '');

  const handleBlur = (markdown: string): void => {
    field.onChange(markdown);
    onValueChange?.(markdown);
  };

  const editor = useMarkdownTiptap({
    value: localValue ?? '',
    onUpdate: (markdown) => {
      setLocalValue(markdown);
    },
    onBlur: handleBlur,
    editorClassName: cn(
      'prose prose-sm h-full overflow-y-auto rounded-b-lg border bg-background p-2 whitespace-normal focus:outline-none [&_li]:my-0 [&_li]:py-0 [&_li_p]:m-0 [&_li_p]:p-0 [&_p]:mb-[1em]',
      showRawMarkdownState === 'split' && '!rounded-r-none'
    ),
    placeholder,
    characterLimit: maxLength,
    editable: !disabled,
  });

  return (
    <div className='flex w-full flex-col rounded-md bg-accent'>
      {editor && (
        <MarkdownToolbar
          editor={editor}
          setRawState={setShowRawMarkdownState}
          rawState={showRawMarkdownState}
        />
      )}
      <div className='flex'>
        <EditorContent
          editor={editor}
          disabled={disabled}
          className={cn(
            'h-[250px] w-full',
            showRawMarkdownState === 'split'
              ? 'w-1/2'
              : showRawMarkdownState === 'full'
                ? 'hidden'
                : ''
          )}
        />
        <TextArea
          onChange={({ target: { value } }) => {
            setLocalValue(value);
          }}
          onBlur={() => handleBlur(localValue)}
          value={localValue ?? ''}
          disabled={disabled}
          className={cn(
            'h-[250px] resize-none rounded-t-none',
            'focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0',
            showRawMarkdownState === 'split'
              ? 'w-1/2 rounded-l-none border-l-0'
              : showRawMarkdownState === 'full'
                ? 'w-full'
                : 'hidden'
          )}
          maxLength={3000}
        />
      </div>
    </div>
  );
}

export function Markdown<Instance extends BaseInstance = BaseInstance>({
  formField,
  name,
  placeholder,
  label,
  description,
  className,
  disabled,
  overrideRequired,
  canHandleField,
  overrideMaxLength,
  onValueChange,
}: FieldProps<Instance> & { onValueChange?: (value: unknown) => void }) {
  return (
    <FormField
      rules={{
        required:
          (overrideRequired != null ? overrideRequired : formField.required) &&
          'Detta fält är obligatoriskt',
      }}
      control={formField.control}
      name={name as Path<Instance>}
      render={({ field }) => (
        <FormItem
          className={cn('col-span-full flex w-full flex-col gap-1', className)}
        >
          <div className='shrink-0'>
            <div className='flex items-center'>
              <FormLabel>
                {label ?? formField.label}
                {(overrideRequired != null
                  ? overrideRequired
                  : formField.required) && <span>*</span>}
              </FormLabel>

              {(description || formField.description) && (
                <DescriptionTooltip
                  description={description ?? formField.description}
                />
              )}
            </div>

            <FormMessage />
          </div>
          <FormControl>
            <MarkdownEditorContent
              field={field}
              placeholder={placeholder ?? (label as string) ?? formField.label}
              maxLength={overrideMaxLength}
              disabled={disabled ?? field.disabled ?? !canHandleField}
              onValueChange={onValueChange}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
}
